import { useEffect, useMemo, useState } from 'react';
import { apiBaseOrigin, configuredApiOrigin } from '../../../shared/config/api-origin';
import { readMissionWorkspace, workspaceQuery } from '../consumer/reader';
import { projectMissionWorkspace, type WorkspaceProjection } from '../domain/projection';
import { createLiveMissionTemplateAdapter } from '../template/adapters/live-mission-template-adapter';
import {
  pinFields,
  type ExactPin,
  type MissionTemplateReadPort,
} from '../template/consumer/contract';
import { MissionWorkspaceView } from './MissionWorkspaceView';
import './mission-workspace.css';

export function MissionWorkspacePanel({
  port,
  query = workspaceQuery,
}: {
  readonly port: MissionTemplateReadPort | null;
  readonly query?: ExactPin;
}) {
  const requestKey = JSON.stringify(pinFields.map((field) => query[field]));
  const [completed, setCompleted] = useState<Readonly<{
    port: MissionTemplateReadPort;
    query: ExactPin;
    requestKey: string;
    projection: WorkspaceProjection;
  }> | null>(null);
  useEffect(() => {
    if (!port) return;
    let current = true;
    setCompleted(null);
    void readMissionWorkspace(port, query).then((observation) => {
      if (current)
        setCompleted({
          port,
          query,
          requestKey,
          projection: projectMissionWorkspace(query, observation),
        });
    });
    return () => {
      current = false;
    };
  }, [port, query, requestKey]);
  const state =
    completed?.port === port && completed?.query === query && completed?.requestKey === requestKey
      ? completed.projection
      : projectMissionWorkspace(query, { status: port ? 'LOADING' : 'NOT_CONNECTED' });
  return <MissionWorkspaceView state={state} />;
}

/** Reads the configured API in a `main` build, otherwise the local loopback origin in dev. */
export function MissionWorkspacePage() {
  const port = useMemo(() => {
    if (typeof window === 'undefined') return null;
    if (!import.meta.env.DEV && configuredApiOrigin() === null) return null;
    try {
      return createLiveMissionTemplateAdapter({ baseOrigin: apiBaseOrigin() });
    } catch {
      return null;
    }
  }, []);
  return <MissionWorkspacePanel port={port} />;
}
